import * as React from "react";
import styled from "styled-components";
import { useContext } from "react";
import { Formik, Form, Field } from "formik";
import { useTranslation } from "react-i18next";
import { validateField, formHasErrors } from "util/FormsUtils";
import { FormikFormGroup, FormikInput, InputOptionalInfo as Opt } from "components/common";
import { Input, Button, ButtonToolbar } from "components/bootstrap";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import BackendWizardContext from "./BackendWizardContext";
export const STEP_KEY = "server-configuration";
export const FORM_VALIDATION = {
  title: {
    required: true
  },
  serverHost: {
    required: true
  },
  serverPort: {
    required: true,
    min: 1,
    max: 65535
  },
  description: {},
  transportSecurity: {},
  verifyCertificates: {},
  systemUserDn: {},
  systemUserPassword: {}
};
const ServerUrl = styled.div`
  display: flex;

  > * {
    align-self: flex-start;
    min-height: 34px;
    flex-grow: 1;

    &:last-child {
      flex: 0.8;
      min-width: 130px;
    }
  }

  .input-group-addon {
    display: flex;
    align-items: center;
    max-width: fit-content;
    min-width: fit-content;
  }
`;
const ProtocolOptions = styled.div`
  display: flex;

  div + * {
    margin-left: 10px;
  }
`;
const ServerConfigStep = ({ formRef, help = {}, onSubmit, onSubmitAll, submitAllError, validateOnMount }) => {
  const { setStepsState, ...stepsState } = useContext(BackendWizardContext);
  const {
    backendValidationErrors,
    authBackendMeta: { backendHasPassword }
  } = stepsState;
  const sendTelemetry = useSendTelemetry("directory-service");
  const { t } = useTranslation("authentication");
  const _onTransportSecurityChange = (event, values, setFieldValue, onChange) => {
    const currentValue = values.transportSecurity;
    const newValue = event.target.value;
    const defaultPort = 389;
    const defaultTlsPort = 636;
    if (currentValue === "tls" && newValue !== "tls" && values.serverPort === defaultTlsPort) {
      setFieldValue("serverPort", defaultPort);
    }
    if (currentValue !== "tls" && newValue === "tls" && values.serverPort === defaultPort) {
      setFieldValue("serverPort", defaultTlsPort);
    }
    onChange(event);
  };
  const _onSubmitAll = (validateForm) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.AUTHENTICATION.DIRECTORY_SERVER_CONFIG_SAVE_CLICKED, {
      app_action_value: "server-configuration-save"
    });
    validateForm().then((errors) => {
      if (!formHasErrors(errors)) {
        onSubmitAll();
      }
    });
  };
  return /* @__PURE__ */ React.createElement(
    Formik,
    {
      initialValues: stepsState.formValues,
      innerRef: formRef,
      initialErrors: backendValidationErrors,
      onSubmit,
      validateOnBlur: false,
      validateOnChange: false,
      validateOnMount
    },
    ({ isSubmitting, setFieldValue, values, validateForm }) => /* @__PURE__ */ React.createElement(Form, { className: "form form-horizontal" }, /* @__PURE__ */ React.createElement(
      FormikFormGroup,
      {
        help: help.title,
        label: t("server_config.title_label"),
        name: "title",
        placeholder: t("server_config.title_label")
      }
    ), /* @__PURE__ */ React.createElement(
      FormikFormGroup,
      {
        help: help.description,
        label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("server_config.description_label"), " ", /* @__PURE__ */ React.createElement(Opt, null)),
        type: "textarea",
        name: "description",
        placeholder: t("server_config.description_label")
      }
    ), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "uri-host",
        label: t("server_config.server_address"),
        labelClassName: "col-sm-3",
        wrapperClassName: "col-sm-9"
      },
      /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(ServerUrl, { className: "input-group" }, /* @__PURE__ */ React.createElement(
        FormikInput,
        {
          formGroupClassName: "",
          name: "serverHost",
          id: "serverHost",
          error: backendValidationErrors == null ? void 0 : backendValidationErrors.serverHost,
          placeholder: t("server_config.hostname"),
          validate: validateField(FORM_VALIDATION.serverHost)
        }
      ), /* @__PURE__ */ React.createElement("span", { className: "input-group-addon input-group-separator" }, ":"), /* @__PURE__ */ React.createElement(
        FormikInput,
        {
          formGroupClassName: "",
          name: "serverPort",
          id: "serverPort",
          error: backendValidationErrors == null ? void 0 : backendValidationErrors.serverPort,
          placeholder: t("server_config.port"),
          type: "number",
          validate: validateField(FORM_VALIDATION.serverPort)
        }
      )), /* @__PURE__ */ React.createElement(ProtocolOptions, null, /* @__PURE__ */ React.createElement(Field, { name: "transportSecurity" }, ({ field: { name, onChange, onBlur, value } }) => /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
        Input,
        {
          defaultChecked: value === "none",
          formGroupClassName: "",
          id: name,
          label: t("server_config.transport_security_none"),
          onBlur,
          onChange: (e) => _onTransportSecurityChange(e, values, setFieldValue, onChange),
          type: "radio",
          value: "none"
        }
      ), /* @__PURE__ */ React.createElement(
        Input,
        {
          defaultChecked: value === "tls",
          formGroupClassName: "",
          id: name,
          label: "TLS",
          onBlur,
          onChange: (e) => _onTransportSecurityChange(e, values, setFieldValue, onChange),
          type: "radio",
          value: "tls"
        }
      ), /* @__PURE__ */ React.createElement(
        Input,
        {
          defaultChecked: value === "start_tls",
          formGroupClassName: "",
          id: name,
          label: "StartTLS",
          onBlur,
          onChange: (e) => _onTransportSecurityChange(e, values, setFieldValue, onChange),
          type: "radio",
          value: "start_tls"
        }
      ))), /* @__PURE__ */ React.createElement(
        FormikInput,
        {
          formGroupClassName: "",
          label: t("server_config.verify_certificates"),
          name: "verifyCertificates",
          id: "verifyCertificates",
          type: "checkbox"
        }
      )))
    ), /* @__PURE__ */ React.createElement(
      FormikFormGroup,
      {
        help: help.systemUserDn,
        error: backendValidationErrors == null ? void 0 : backendValidationErrors.systemUserDn,
        label: t("server_config.system_user_dn"),
        name: "systemUserDn",
        validate: validateField(FORM_VALIDATION.systemUserDn),
        placeholder: t("server_config.system_user_dn"),
        required: true
      }
    ), backendHasPassword && values.systemUserPassword === void 0 ? /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "systemPassword",
        label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("server_config.system_password"), " ", /* @__PURE__ */ React.createElement(Opt, null)),
        labelClassName: "col-sm-3",
        wrapperClassName: "col-sm-9"
      },
      /* @__PURE__ */ React.createElement(Button, { type: "button", onClick: () => setFieldValue("systemUserPassword", "") }, t("server_config.reset_password"))
    ) : /* @__PURE__ */ React.createElement(
      FormikFormGroup,
      {
        autoComplete: "authentication-service-password",
        buttonAfter: backendHasPassword && values.systemUserPassword !== void 0 ? /* @__PURE__ */ React.createElement(Button, { type: "button", onClick: () => setFieldValue("systemUserPassword", void 0) }, t("server_config.undo_reset")) : void 0,
        help: help.systemUserPassword,
        label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("server_config.system_password"), " ", /* @__PURE__ */ React.createElement(Opt, null)),
        name: "systemUserPassword",
        error: backendValidationErrors == null ? void 0 : backendValidationErrors.systemUserPassword,
        placeholder: t("server_config.system_password"),
        validate: validateField(FORM_VALIDATION.systemUserPassword),
        type: "password"
      }
    ), submitAllError, /* @__PURE__ */ React.createElement(ButtonToolbar, { className: "pull-right" }, /* @__PURE__ */ React.createElement(Button, { disabled: isSubmitting, onClick: () => _onSubmitAll(validateForm), type: "button" }, t("wizard.finish_and_save")), /* @__PURE__ */ React.createElement(
      Button,
      {
        bsStyle: "primary",
        disabled: isSubmitting,
        onClick: () => {
          sendTelemetry(TELEMETRY_EVENT_TYPE.AUTHENTICATION.DIRECTORY_NEXT_USER_SYNC_CLICKED, {
            app_action_value: "usersync-button"
          });
        },
        type: "submit"
      },
      t("wizard.next_user_sync")
    )))
  );
};
export default ServerConfigStep;
